import LanguageSwitcher from '@/components/LanguageSwitcher';
import SimpleIconsAlipay from '@/components/icons/SimpleIconsAlipay';
import '@/styles/auth-success-animations.css';
import { ALL_LANGUAGES, tolgee } from '@/tolgee/shared';
import Box from '@mui/material/Box';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';

interface PageProps {
  locale: string;
  pathname: string;
  translations: {
    title: string;
    description: string;
    instruction: string;
    security_note: string;
    meta: {
      title: string;
      description: string;
      keywords: string;
    };
  };
}

export default function AlipayAuthSuccessPage({ translations: t, locale, pathname }: PageProps) {
  return (
    <div className="min-h-[calc(100vh-80px)] bg-n-8 flex items-center justify-center p-4 relative overflow-hidden">
      <Head>
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <meta name="keywords" content={t.meta.keywords} />
      </Head>
      {/* 语言切换器 - 右上角 */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher />
      </div>

      {/* 背景装饰元素 */}
      <div className="absolute -left-[10%] top-[20%] h-[300px] w-[300px] rounded-full bg-color-primary/5 blur-[100px]" />
      <div className="absolute -right-[10%] bottom-[20%] h-[300px] w-[300px] rounded-full bg-color-secondary/5 blur-[100px]" />

      {/* 主要内容 */}
      <div className="w-fit min-w-96 animate-fade-in-up">
        <div className="bg-n-7/80 border border-n-6 backdrop-blur-sm shadow-2xl rounded-2xl p-8">
          {/* Logo区域 - 使用Grid布局确保中间图标居中 */}
          <div className="mb-12 animate-scale-in">
            <div className="grid grid-cols-3 items-center gap-0 w-full max-w-2xl mx-auto">
              {/* CrowdComputed Logo - 左列 */}
              <div className="flex items-center justify-center">
                <img src="/assets/logo/logo.svg" alt="CrowdComputed" className="h-10 w-auto max-w-full" />
              </div>

              {/* 中间的授权图标 - 中列，绝对居中 */}
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <div className="w-8 h-px bg-gradient-to-r from-n-6 to-color-primary/50"></div>
                  <div className="relative mx-3">
                    <div className="absolute inset-0 rounded-full bg-color-primary/20 animate-pulse"></div>
                    <div className="relative z-10 p-2 rounded-full bg-gradient-to-br from-color-primary to-yellow-500">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-5-5 1.41-1.41L11 14.17l7.59-7.59L20 8l-9 9z" />
                      </svg>
                    </div>
                  </div>
                  <div className="w-8 h-px bg-gradient-to-r from-color-primary/50 to-n-6"></div>
                </div>
              </div>

              {/* 支付宝 Logo - 右列 */}
              <div className="flex items-center justify-center">
                <Box
                  component={SimpleIconsAlipay}
                  className="h-10 w-10"
                  style={{
                    filter:
                      'brightness(0) saturate(100%) invert(35%) sepia(100%) saturate(1500%) hue-rotate(200deg) brightness(95%) contrast(105%)',
                  }}
                />
              </div>
            </div>
          </div>

          {/* 成功状态展示 */}
          <div className="text-center mb-8 animate-fade-in-up-delay-1">
            <h1 className="text-3xl font-bold text-n-1 mb-4">
              <span className="bg-gradient-to-r from-color-primary via-yellow-500 to-color-primary bg-clip-text text-transparent">
                {t.title}
              </span>
            </h1>
            <p className="text-n-3 text-base leading-relaxed">{t.description}</p>
          </div>

          {/* 简化的操作提示 */}
          <div className="text-center animate-fade-in-up-delay-2">
            <div className="p-6 rounded-xl bg-n-6/30 border border-n-5/30">
              <p className="text-n-2 text-base font-medium">{t.instruction}</p>
            </div>
          </div>

          {/* 底部安全提示 */}
          <div className="mt-7 text-center animate-fade-in-delay-3">
            <p className="text-xs text-n-4">{t.security_note}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = ALL_LANGUAGES.map((locale) => ({
    params: { locale },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  const locale = params?.locale as string;

  const { t, run } = tolgee(locale);

  await run();

  const translations = {
    title: t('payment_auth_success.title'),
    description: t('payment_auth_success.description', { provider: t('alipay.name') }),
    instruction: t('payment_auth_success.instruction'),
    security_note: t('payment_auth_success.security_note'),
    meta: {
      title: t('provider_page.payment_success.meta.title', { provider: t('alipay.name') }),
      description: t('provider_page.payment_success.meta.desc', { provider: t('alipay.name') }),
      keywords: t('provider_page.meta.keywords'),
    },
    common: {
      policies: t('common.policies'),
    },
  };

  return {
    props: {
      locale,
      pathname: '/alipay/auth/success',
      translations,
    },
    // 重新生成时间：30天
    revalidate: 3600 * 24 * 30,
  };
};
